import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import ProfileDialog, { Avatar } from "@/components/ProfileDialog";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  LayoutDashboard, Receipt, CalendarClock, ClockAlert, RotateCcw,
  Building2, Scissors, FileText, Users, LogOut, Menu, ListChecks, Search, History, PanelLeftClose, PanelLeftOpen, X,
} from "lucide-react";
import { BookOpen } from "lucide-react";

const NAV = [
  { label: "Dashboard", icon: LayoutDashboard, path: "/" },
  { label: "Faktur Pembelian", icon: Receipt, path: "/faktur" },
  { label: "Penjadwalan Pembayaran", icon: CalendarClock, path: "/pembayaran" },
  { label: "Pembayaran Terjadwal", icon: ListChecks, path: "/pembayaran-terjadwal" },
  { label: "Reschedule", icon: ClockAlert, path: "/reschedule" },
  { label: "Riwayat Reschedule", icon: History, path: "/riwayat-reschedule" },
  { label: "Persetujuan", icon: ClockAlert, path: "/persetujuan", owner: true },
  { label: "Retur & Kredit", icon: RotateCcw, path: "/retur" },
  { label: "Supplier", icon: Building2, path: "/suppliers" },
  { label: "Master Jenis Kain", icon: Scissors, path: "/kain" },
  { label: "Buku Besar", icon: BookOpen, path: "/buku-besar" },
  { label: "Laporan & Export", icon: FileText, path: "/laporan" },
  { label: "Akun Pengguna", icon: Users, path: "/pengguna", owner: true },
];

function Brand({ mini }) {
  return (
    <div className={`flex items-center gap-2.5 pb-5 mb-1 border-b transition-[padding,border-color] duration-700 ease-[cubic-bezier(0.65,0,0.35,1)] ${mini ? "pt-0 pl-[7px] pr-0 border-transparent" : "pt-4 pl-2.5 pr-6 border-slate-100"}`}>
      <img src="/logo-meranggi.png" alt="Meranggi Garment" className="h-11 w-11 shrink-0 rounded-full object-cover shadow-md shadow-slate-900/15" data-testid="brand-logo" />
      <div className={`overflow-hidden whitespace-nowrap transition-[max-width,opacity] duration-700 ease-[cubic-bezier(0.65,0,0.35,1)] ${mini ? "max-w-0 opacity-0" : "max-w-[200px] opacity-100"}`}>
        <div className="font-extrabold leading-tight text-slate-900 tracking-tight whitespace-nowrap">Meranggi Garment</div>
        <div className="text-[11px] text-slate-400 font-medium">CV. Meranggi Jaya</div>
      </div>
    </div>
  );
}

function SidebarContent({ isOwner, onNav, inSheet, mini }) {
  return (
    <nav className={`flex flex-col p-3 ${mini ? "gap-2.5 pt-[9px]" : "gap-1"} ${inSheet ? "pt-14" : ""}`}>
      <Brand mini={mini} />
      {NAV.filter((n) => !n.owner || isOwner).map((n) => (
        <NavLink
          key={n.path}
          to={n.path}
          end={n.path === "/"}
          onClick={onNav}
          title={mini ? n.label : undefined}
          data-testid={`nav-${n.path === "/" ? "dashboard" : n.path.slice(1)}`}
          className={({ isActive }) =>
            `flex items-center text-sm overflow-hidden transition-[width,height,padding,border-radius,background-color,color,box-shadow,transform] duration-700 ease-[cubic-bezier(0.65,0,0.35,1)] ${
              mini ? "h-11 w-11 ml-[4px] px-[13px] shrink-0 rounded-full border shadow-[0_6px_16px_-6px_rgba(15,23,42,0.18)] hover:scale-110 hover:-translate-y-0.5" : "h-10 w-full px-3.5 rounded-2xl border border-transparent"
            } ${
              isActive
                ? mini ? "bg-slate-900 text-white border-slate-900" : inSheet ? "bg-white/70 text-slate-900 font-semibold shadow-sm" : "bg-slate-100 text-slate-900 font-semibold"
                : mini ? "bg-white border-slate-200 text-slate-500 hover:text-slate-900" : inSheet ? "text-slate-700 hover:bg-white/50 font-medium" : "text-slate-500 hover:bg-slate-50 hover:text-slate-900 font-medium"
            }`
          }
        >
          <n.icon className="h-[18px] w-[18px] shrink-0" />
          <span className={`whitespace-nowrap transition-[opacity,margin] duration-500 ease-[cubic-bezier(0.65,0,0.35,1)] ${mini ? "opacity-0 ml-0" : "opacity-100 ml-3"}`}>{n.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

export default function Layout({ children, title }) {
  const { user, logout, isOwner } = useAuth();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const onSearchKey = (e) => {
    if (e.key === "Enter" && search.trim()) {
      navigate(`/faktur?q=${encodeURIComponent(search.trim())}`);
      setSearch("");
    }
  };

  const [profileOpen, setProfileOpen] = useState(false);
  const [mini, setMini] = useState(() => localStorage.getItem("sidebar-mini") === "1");
  useEffect(() => { localStorage.setItem("sidebar-mini", mini ? "1" : "0"); }, [mini]);

  return (
    <div className="min-h-screen bg-[#F4F5F7]">
      <aside className={`hidden lg:block fixed top-4 bottom-4 left-4 rounded-[32px] overflow-y-auto overflow-x-hidden transition-[width,background-color,border-color,box-shadow] duration-700 ease-[cubic-bezier(0.65,0,0.35,1)] ${mini ? "w-[84px] bg-transparent border border-transparent shadow-none" : "w-64 bg-white border border-slate-200/70 shadow-[0_8px_30px_-6px_rgba(15,23,42,0.08)]"}`} data-testid="app-sidebar">
        <SidebarContent isOwner={isOwner} mini={mini} />
      </aside>

      <div className={`transition-[padding] duration-700 ease-[cubic-bezier(0.65,0,0.35,1)] ${mini ? "lg:pl-[100px]" : "lg:pl-[17rem]"}`}>
        {/* Header */}
        <header className="sticky top-3 lg:top-4 z-30 mx-3 sm:mx-6 lg:mx-8 mt-3 lg:mt-4 h-16 bg-white/85 backdrop-blur-xl rounded-full border border-slate-200/70 shadow-[0_8px_30px_-8px_rgba(15,23,42,0.08)] flex items-center gap-3 px-3 sm:px-6" data-testid="app-header">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" hideClose className="liquid-glass p-0 !inset-2 !h-auto !w-auto !max-w-none rounded-[44px] overflow-y-auto">
              <button type="button" onClick={() => setOpen(false)} className="absolute right-4 top-4 h-10 w-10 rounded-full bg-white/60 border border-white/70 backdrop-blur-xl flex items-center justify-center text-slate-700 shadow-sm active:scale-95 transition-transform" data-testid="mobile-menu-close"><X className="h-5 w-5" /></button>
              <SidebarContent isOwner={isOwner} onNav={() => setOpen(false)} inSheet />
            </SheetContent>
          </Sheet>
          <Button variant="ghost" size="icon" className="hidden lg:inline-flex rounded-full text-slate-500 hover:text-slate-900" onClick={() => setMini((m) => !m)} data-testid="sidebar-toggle">
            {mini ? <PanelLeftOpen className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
          </Button>

          <h1 className="text-lg sm:text-xl font-extrabold tracking-tight text-slate-900 shrink-0" data-testid="page-title">{title}</h1>

          <div className="hidden md:block flex-1 max-w-sm ml-2">
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={onSearchKey}
                placeholder="Cari no. nota…"
                data-testid="global-search"
                className="w-full bg-slate-100/80 hover:bg-slate-100 focus:bg-white border border-transparent focus:border-indigo-400 rounded-full pl-10 pr-4 py-2 text-sm outline-none transition-all placeholder:text-slate-400"
              />
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 ml-auto">
            <button type="button" onClick={() => setProfileOpen(true)} className="flex items-center gap-2.5 bg-white border border-slate-200/80 rounded-full pl-1 pr-1 sm:pr-3.5 py-1 shadow-sm hover:bg-slate-50 transition-colors" data-testid="user-chip">
              <Avatar user={user} />
              <div className="text-left hidden sm:block leading-tight">
                <div className="text-[13px] font-bold text-slate-900">{user?.name}</div>
                <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{{ owner: "Owner", accounting: "Accounting" }[user?.role] || "Staf"}</div>
              </div>
            </button>
            <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} />
            <Button variant="ghost" size="icon" className="rounded-full text-slate-400 hover:text-slate-900 hover:bg-slate-100" onClick={doLogout} data-testid="logout-btn">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </header>

        <main className="px-3 py-4 sm:p-6 lg:p-8 space-y-6">{children}</main>
        <footer className="flex items-center justify-center gap-1.5 pb-5 pt-1 select-none" data-testid="powered-by">
          <span className="text-[10px] tracking-wide text-slate-400">powered by</span>
          <img src="/gigazip.png" alt="gigazip" className="h-2.5 w-auto opacity-40 grayscale hover:grayscale-0 hover:opacity-90 transition-[opacity,filter] duration-300" />
        </footer>
      </div>
    </div>
  );
}
